import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Sparkles, Check, Brain, Headphones, Globe, Database, Zap, Shield, Code, Mail, UserCog } from 'lucide-react';
import { aiImageFeatureForPackage } from '../../../utils/packageAiImagesFeature';
import PackagePlanFooter from '../../billing/PackagePlanFooter';

const PremiumView = () => {
  const { t } = useTranslation();
  const { user } = useSelector((state) => state.auth);
  const userPackage = user?.result?.package || 1; // Default to 1 (free)
  const isCurrentPackage = userPackage === 5; // Premium = 5

  const features = [
    { included: true, text: t('packages.premium.feature1', 'Neograničeno adresa') },
    { included: true, text: t('packages.premium.feature2', 'SMS slanje strankama') },
    { included: true, text: t('packages.premium.feature3', 'Web Shop kreiranje') },
    aiImageFeatureForPackage(t, 'premium'),
    { included: true, text: t('packages.featureVoiceInput', 'Glasovni unos podataka') },
    { included: true, text: t('packages.premium.feature4', 'AI model za optimizaciju') },
    { included: true, text: t('packages.premium.feature5', 'Potpuni dashboard') },
    { included: true, text: t('packages.premium.feature6', 'Napredno upravljanje poslovima') },
    { included: true, text: t('packages.premium.feature7', 'Prioritetni support') },
    { included: true, text: t('packages.premium.feature8', 'Individualne web stranice') },
    { included: true, text: t('packages.premium.feature9', 'Custom baze podataka') },
    { included: true, text: t('packages.premium.feature10', 'Dedicated account manager') },
    { included: true, text: t('packages.premium.feature11', 'API pristup za integracije') },
    { included: true, text: t('packages.premium.feature12', 'White-label opcije') }
  ];

  const premiumFeatures = [
    {
      icon: <Headphones className="h-6 w-6 text-yellow-400" />,
      title: 'Prioritetna Podrška',
      description: 'Lična komunikacija sa našim timom. Brzi odgovori, direktna veza sa developerima i prioritetno rešavanje problema.',
      highlight: true
    },
    {
      icon: <Globe className="h-6 w-6 text-yellow-400" />,
      title: 'Individualne Web Stranice',
      description: 'Izrada custom web stranica van aplikacije, prilagođenih vašim potrebama i brendu. Potpuna kontrola dizajna i funkcionalnosti.',
      highlight: true
    },
    {
      icon: <Database className="h-6 w-6 text-yellow-400" />,
      title: 'Custom Baze Podataka',
      description: 'Prilagođene baze podataka sa specifičnim poljima i strukturom koje odgovaraju vašem poslovanju.',
      highlight: true
    },
    {
      icon: <Brain className="h-6 w-6 text-yellow-400" />,
      title: 'Napredne AI Funkcionalnosti',
      description: 'AI model za optimizaciju rutiranja, predikciju zaliha, analizu trendova i pametnu alokaciju resursa.',
      highlight: false
    },
    {
      icon: <Code className="h-6 w-6 text-yellow-400" />,
      title: 'API Pristup',
      description: 'Potpuni API pristup za integracije sa drugim sistemima i automatizaciju poslovnih procesa.',
      highlight: false
    },
    {
      icon: <UserCog className="h-6 w-6 text-yellow-400" />,
      title: 'Dedicated Account Manager',
      description: 'Dodeljeni account manager koji vam pomaže sa optimizacijom i prilagođavanjem sistema.',
      highlight: false
    },
    {
      icon: <Shield className="h-6 w-6 text-yellow-400" />,
      title: 'White-Label Opcije',
      description: 'Mogućnost prilagođavanja brendiranja aplikacije sa vašim logoom i bojama.',
      highlight: false
    },
    {
      icon: <Zap className="h-6 w-6 text-yellow-400" />,
      title: 'Prioritetni Deployment',
      description: 'Brže implementacije novih funkcionalnosti i prioritetno rešavanje feature requestova.',
      highlight: false
    }
  ];

  return (
    <div className="bg-gray-900 min-h-screen p-4 sm:p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="bg-gray-800 rounded-xl shadow-xl p-6 border border-yellow-700">
          <div className="flex items-center gap-4 mb-4">
            <div className="bg-yellow-500/20 rounded-lg p-4">
              <Sparkles className="h-8 w-8 text-yellow-400" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-white">{t('packages.premium.title', 'Premium Paket')}</h1>
              <p className="text-gray-400 mt-1">{t('packages.premium.subtitle', 'Najnapredniji paket sa AI funkcionalnostima')}</p>
            </div>
          </div>
          
          {isCurrentPackage && (
            <div className="bg-green-900/30 border border-green-700 rounded-lg p-4 mt-4">
              <div className="flex items-center gap-2 text-green-400">
                <Check className="h-5 w-5" />
                <span className="font-medium">{t('packages.currentPackage', 'Trenutni paket')}</span>
              </div>
            </div>
          )}
        </div>

        {/* Features */}
        <div className="bg-gray-800 rounded-xl shadow-xl p-6 border border-gray-700">
          <h2 className="text-xl font-bold text-white mb-4">{t('packages.features', 'Karakteristike')}</h2>
          <div className="space-y-3">
            {features.map((feature, index) => (
              <div key={index} className={feature.detail ? 'space-y-1.5' : ''}>
                <div className="flex items-center gap-3">
                  <Check className="h-5 w-5 text-green-400 flex-shrink-0" />
                  <span className="text-white">{feature.text}</span>
                </div>
                {feature.detail && (
                  <p className="text-sm text-amber-200/90 leading-snug pl-8 border-l-2 border-amber-500/35 ml-1">
                    {feature.detail}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>

        <PackagePlanFooter packageId={5} theme="yellow" />

        {/* Premium Exclusive Features */}
        <div className="bg-gray-800 rounded-xl shadow-xl p-6 border border-yellow-700">
          <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
            <Sparkles className="h-6 w-6 text-yellow-400" />
            Premium Ekskluzivne Funkcionalnosti
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {premiumFeatures.map((feature, index) => (
              <div
                key={index}
                className={`p-4 rounded-lg border ${
                  feature.highlight
                    ? 'bg-yellow-900/20 border-yellow-700'
                    : 'bg-gray-700/50 border-gray-600'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 mt-1">
                    {feature.icon}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-white mb-1">{feature.title}</h3>
                    <p className="text-sm text-gray-300">{feature.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Priority Support Highlight */}
        <div className="bg-gradient-to-r from-yellow-900/30 to-orange-900/30 border border-yellow-700 rounded-xl p-6">
          <div className="flex items-start gap-4">
            <div className="bg-yellow-500/20 rounded-lg p-3 flex-shrink-0">
              <Headphones className="h-8 w-8 text-yellow-400" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-white mb-2">Prioritetna Podrška</h3>
              <p className="text-gray-300 mb-4">
                Kao Premium korisnik, imate direktnu vezu sa našim timom. Rešavamo vaše probleme prioritetno, 
                pružamo ličnu komunikaciju i brze odgovore. Vaš dedicated account manager je uvek dostupan 
                da vam pomogne sa optimizacijom i prilagođavanjem sistema vašim potrebama.
              </p>
              <div className="bg-gray-800/50 rounded-lg p-4 mt-4">
                <p className="text-sm text-gray-300">
                  <strong className="text-yellow-400">Kontakt:</strong> Pošaljite email na{' '}
                  <a href="mailto:premium@spintasker.com" className="text-yellow-400 hover:underline">
                    premium@spintasker.com
                  </a>{' '}
                  ili koristite direktnu chat podršku u aplikaciji.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PremiumView;
